import React, { useMemo } from 'react'
import { css } from '@emotion/react'
import { Question } from '@/features/survey/survey-types'
import { useFormContext } from "react-hook-form";
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '@/app/hooks'
import { ErrorMessage } from '@/components/atoms/error-message'

type Props = {
  question: Question
  index: number
}

export const SurveyTextForm = ({ question, index }: Props) => {
  const { register, formState: { errors }, watch } = useFormContext()
  const { t } = useTranslation()
  const design = useAppSelector(state => state.design)

  // 文字数制限（固定質問は1000文字、その他は400文字）
  const maxLength = 400

  // エラーメッセージの取得
  const errorMessage = errors.answers?.[index]?.answerText?.message as string | undefined

  const fieldName = `answers.${index}.answerText`
  // 現在の文字数を監視（リアルタイム表示用）
  const currentValue = watch(fieldName) || ''
  const currentLength = currentValue.length

  const textAreaStyle = useMemo(
    () => css({
      width: '100%',
      minHeight: '120px',
      padding: '12px 16px',
      border: `1px solid ${errorMessage ? '#E6483B' : design?.form.borderColor}`,
      borderRadius: '8px',
      fontSize: '14px',
      lineHeight: '1.5',
      color: design?.form.textColor,
      backgroundColor: design?.background.cardColor,
      resize: 'vertical',
      '&::placeholder': {
        color: '#CCCCCC'
      }
    }),
    [design, errorMessage],
  )

  const charCountStyle = useMemo(
    () => css({
      fontSize: '12px',
      color: currentLength > maxLength ? '#E6483B' : design?.theme.brandColor,
      marginTop: '0.25rem',
      textAlign: 'right',
    }),
    [design, currentLength, maxLength],
  )
  
  return (
    <>
      <textarea
        {...register(fieldName, {
          validate: (value) => {
            // ネストされた配列フィールドではvalueがundefinedになることがあるため、watchで最新値を取得
            const actualValue = watch(fieldName) ?? ''
            const textValue = String(actualValue || '')
            
            // 必須チェック
            if (question.isRequired && !textValue.trim()) {
              return t('This field is required')
            }
            // 文字数チェック
            if (textValue && textValue.length > maxLength) {
              return t('Maximum {{count}} characters', { count: maxLength })
            }
            return true
          }
        })}
        placeholder={t('Please fill in this field')}
        css={textAreaStyle}
        rows={4}
      />
      <div css={charCountStyle}>
        {currentLength} / {maxLength}
      </div>
      {errorMessage && <ErrorMessage text={errorMessage} />}
    </>
  )
}

